// ============================================================
// THE COMPANY — CopyButton Component
// ============================================================

import { useState, useCallback, useRef, useEffect } from "react";

interface CopyButtonProps {
  text: string;
  label?: string;
}

export default function CopyButton({ text, label = "Copy" }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      // Clear any existing timer
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        setCopied(false);
        timerRef.current = null;
      }, 2000);
    } catch {
      // Fallback: silently fail if clipboard is unavailable
    }
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`
        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
        text-sm font-medium transition-all duration-150
        ${
          copied
            ? "bg-green-600/20 text-green-400 border border-green-600/30"
            : "bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] border border-transparent"
        }
      `}
    >
      {copied ? (
        <>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="2 7 5.5 10.5 12 4" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="4.5" y="4.5" width="8" height="8" rx="1.5" />
            <path d="M9.5 4.5V2.5A1.5 1.5 0 008 1H2.5A1.5 1.5 0 001 2.5V8a1.5 1.5 0 001.5 1.5h2" />
          </svg>
          {label}
        </>
      )}
    </button>
  );
}
